import { t } from "i18next";
import { useState } from "react";
import { Ban, CheckCircle2, MinusCircle } from "lucide-react";

import { FLASHPAY_ROLES, useRealmRoles } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSetAgentStatus } from "@/features/kyc/api/use-set-agent-status";
import type { AgentDto } from "@/features/kyc/api/dto";
import type { AgentStatus } from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type AgentRow = AgentDto & { id: string };

/** Statuts cibles proposés selon le statut courant de l'agent. */
const nextStatuses = (status?: AgentStatus): AgentStatus[] => {
  switch (status) {
    case "ACTIVE":
      return ["INACTIVE", "SUSPENDED"];
    case "INACTIVE":
      return ["ACTIVE", "SUSPENDED"];
    case "SUSPENDED":
    default:
      return ["ACTIVE"];
  }
};

const STATUS_ACTION_LABEL: Record<AgentStatus, string> = {
  ACTIVE: "kyc.agents.actions.activate",
  INACTIVE: "kyc.agents.actions.deactivate",
  SUSPENDED: "kyc.agents.actions.suspend",
};

const STATUS_ACTION_ICON = {
  ACTIVE: CheckCircle2,
  INACTIVE: MinusCircle,
  SUSPENDED: Ban,
} as const;

/** Menu d'actions de ligne : changement de statut (mutation + feedback). */
export function AgentRowActions({ agent }: { agent: AgentRow }) {
  const setAgentStatus = useSetAgentStatus();
  const agentId = agent.id;
  const [targetStatus, setTargetStatus] = useState<AgentStatus | null>(null);
  // Changement de statut : rôles back-office uniquement (guide §6.2) —
  // sans rôle, pas de menu d'actions du tout.
  const realmRoles = useRealmRoles();
  const canManage = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN].some(
    (role) => realmRoles.includes(role)
  );

  if (!canManage) return null;

  return (
    <>
      <RowActionsMenu
        actions={nextStatuses(agent.status).map((status) => ({
          id: `set-status-${status}`,
          label: t(STATUS_ACTION_LABEL[status]),
          icon: STATUS_ACTION_ICON[status],
          variant:
            status === "SUSPENDED" ? ("destructive" as const) : ("default" as const),
          onClick: () => setTargetStatus(status),
        }))}
      />
      <MutationConfirmPreview
        open={targetStatus !== null}
        onOpenChange={(open) => {
          if (!open) setTargetStatus(null);
        }}
        data={{
          id: agentId,
          fullName: agent.fullName,
          currentStatus: agent.status,
          nextStatus: targetStatus,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          targetStatus ? t(STATUS_ACTION_LABEL[targetStatus]) : undefined
        }
        t={t}
        pending={setAgentStatus.isPending}
        onConfirm={() => {
          if (!targetStatus) return;
          setAgentStatus.mutate(
            { agentId, status: targetStatus },
            {
              onSuccess: () => {
                notifSilence(t("kyc.agents.feedback.statusUpdated"));
                setTargetStatus(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </>
  );
}
